import { Graphics, StrokeInput } from 'pixi.js';
import { CreatorWing, WingDivisionType } from '@/types/creator';
import { hasMosquitoNetRunner } from '@/utils/creatorUtils';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import ConfigurationRenderer from '../ConfigurationRenderer';
import DrawableObject from './DrawableObject';

const { COLORS, WING } = RENDERER_CONFIG;

type WingConfig = Pick<CreatorWing, 'withMosquitoNet'>;

class Wing extends DrawableObject {
	static readonly DIVISION_WIDTH = 5;
	static readonly MOSQUITO_NET_RUNNER_WIDTH = 5;
	static readonly OUTER_FRAME_MARGIN = 90;
	static readonly INNER_FRAME_MARGIN = 50;
	static readonly MOSQUITO_NET_LINE_SPACING = 10;
	static readonly FRAME_STROKE_STYLE: StrokeInput = {
		color: COLORS.GRAY,
		pixelLine: true,
	};

	private mosquitoNetStrokeStyle: StrokeInput;
	private isMosquitoError: boolean;
	private isFirstWing: boolean;
	private isLastWing: boolean;
	private prevWithMosquitoNet: boolean;
	private nextWithMosquitoNet: boolean;
	prevDivisionType?: WingDivisionType;
	nextDivisionType?: WingDivisionType;

	withMosquitoNet: boolean;
	private mosquitoNetMask = new Graphics();

	private frame = new Graphics({
		zIndex: WING.Z_INDEX,
	});
	private mosquitoNet = new Graphics({
		zIndex: WING.Z_INDEX + 1,
	});
	private mosquitoNetRunners = new Graphics({
		zIndex: WING.Z_INDEX + 1,
	});

	constructor(
		renderer: ConfigurationRenderer,
		creatorWingConfig: WingConfig,
		isMosquitoError: boolean,
		isFirstWing = false,
		isLastWing = false,
		prevDivisionType?: WingDivisionType,
		nextDivisionType?: WingDivisionType,
		prevWithMosquitoNet = false,
		nextWithMosquitoNet = false
	) {
		super(renderer);
		this.withMosquitoNet = creatorWingConfig.withMosquitoNet;
		this.isLastWing = isLastWing;
		this.isFirstWing = isFirstWing;

		this.isMosquitoError = isMosquitoError;

		this.prevDivisionType = prevDivisionType;
		this.nextDivisionType = nextDivisionType;
		this.prevWithMosquitoNet = prevWithMosquitoNet;
		this.nextWithMosquitoNet = nextWithMosquitoNet;

		this.frame.setStrokeStyle(Wing.FRAME_STROKE_STYLE);
		this.container.addChild(this.frame);

		this.mosquitoNetStrokeStyle = {
			pixelLine: true,
			alpha: 0.5,
			color: this.isMosquitoError ? COLORS.RED : COLORS.DARK_GRAY,
		};
		this.mosquitoNet.setStrokeStyle(this.mosquitoNetStrokeStyle);
		this.container.addChild(this.mosquitoNet);
		this.mosquitoNetRunners.setStrokeStyle(Wing.FRAME_STROKE_STYLE);
		this.container.addChild(this.mosquitoNetRunners);
		this.container.addChild(this.mosquitoNetMask);
		this.mosquitoNet.mask = this.mosquitoNetMask;
	}

	draw() {
		const outerMargin = Wing.OUTER_FRAME_MARGIN * this.renderer.scale,
			innerMargin = Wing.INNER_FRAME_MARGIN * this.renderer.scale;

		const isMiddleWing = !this.isFirstWing && !this.isLastWing,
			isOnlyWing = this.isFirstWing && this.isLastWing;

		let outerFrameWidht =
			this.scaledDimensions.width - outerMargin * (isMiddleWing ? 1 : 1.5);
		if (isOnlyWing)
			outerFrameWidht = this.scaledDimensions.width - outerMargin * 2;

		let startX = outerMargin;
		if (!this.isFirstWing) startX -= outerMargin / 2;

		this.frame
			.rect(
				startX,
				outerMargin,
				outerFrameWidht,
				this.scaledDimensions.height - outerMargin * 2
			)
			.stroke();

		this.frame
			.rect(
				startX + innerMargin,
				outerMargin + innerMargin,
				outerFrameWidht - innerMargin * 2,
				this.scaledDimensions.height - (outerMargin + innerMargin) * 2
			)
			.stroke();

		if (this.withMosquitoNet) {
			this.drawMosquitoNet();
			this.drawMosquitoNetRunners();
		}
	}

	private drawMosquitoNet() {
		const { width, height } = this.scaledDimensions;
		const step = Wing.MOSQUITO_NET_LINE_SPACING;
		const originX = this.container.x;

		this.mosquitoNetMask.clear().rect(0, 0, width, height).fill(0xffffff);

		this.mosquitoNet.clear();
		this.mosquitoNet.setStrokeStyle(this.mosquitoNetStrokeStyle);

		for (let x = -height - originX; x < width; x += step) {
			this.mosquitoNet.moveTo(x, 0);
			this.mosquitoNet.lineTo(x + height, height);
		}

		for (let x = -originX; x < width + height; x += step) {
			this.mosquitoNet.moveTo(x, 0);
			this.mosquitoNet.lineTo(x - height, height);
		}

		this.mosquitoNet.stroke();
	}

	private drawMosquitoNetRunners() {
		const { width, height } = this.scaledDimensions;
		const runnerWidth = Math.min(Wing.MOSQUITO_NET_RUNNER_WIDTH, width / 2);

		this.mosquitoNetRunners.clear();

		if (hasMosquitoNetRunner(this.prevDivisionType, this.prevWithMosquitoNet))
			this.mosquitoNetRunners
				.rect(0, 0, runnerWidth, height)
				.fill({ color: COLORS.WHITE })
				.stroke();

		if (hasMosquitoNetRunner(this.nextDivisionType, this.nextWithMosquitoNet))
			this.mosquitoNetRunners
				.rect(width - runnerWidth, 0, runnerWidth, height)
				.fill({ color: COLORS.WHITE })
				.stroke();
	}

	override destroy() {
		this.mosquitoNet.mask = null;
		super.destroy();
	}
}

export default Wing;
